import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  forwardRef,
  inject,
  Input,
  OnInit,
  TemplateRef,
} from '@angular/core';
import {
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  Validator,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { AbstractControlValueAccessor } from '../../abstract_control_value_accessor.abstract';
import { publishLast, refCount } from 'rxjs/operators';
import { SettingsService } from '../../../core/settings/settings.service';
import { Settings } from '../../../core/_models/settings';
import { ConsentsTranslations } from '../../models/consents-translations';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { FormErrors } from '@app/shared/types/form-errors';
import { ConsentAnswerDirective } from '@app/shared/components/consents/consent-answer.directive';
import { TranslatePipe } from '@ngx-translate/core';
import { NgClass } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-consents',
  templateUrl: './consents.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => ConsentsComponent), multi: true },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => ConsentsComponent), multi: true },
  ],
  imports: [
    ConsentAnswerDirective,
    ReactiveFormsModule,
    TranslatePipe,
    NgClass,
  ],
})
export class ConsentsComponent extends AbstractControlValueAccessor implements OnInit, Validator {
  @Input() public consentsTranslations?: ConsentsTranslations;
  @Input() public errors: FormErrors;
  @Input() public allowedKeys: string[] | null;
  @Input() public isRegistrationScreen = false;

  public readonly formGroup = new UntypedFormGroup({});
  public consentKeys: string[] = [];
  public readonly settingsObservable = this.settingsService.getSettingsOnce().pipe(
    publishLast(),
    refCount(),
  );
  public settings: Settings;
  public readmoreModalRef: BsModalRef;
  private destroyRef = inject(DestroyRef);

  constructor(private cdr: ChangeDetectorRef,
              private settingsService: SettingsService,
              public modalService: BsModalService) {
    super();
  }

  public ngOnInit(): void {
    this.settingsObservable.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((settings) => {
      this.settings = settings;
      this.onSettingsFetched(this.settings);
    });

    this.formGroup.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      this.onTouched();
      this.onChange(this.formGroup.value);
    });
  }

  public onSettingsFetched(settings: Settings): void {
    const schema = settings.schemaObject;

    schema.consents.availableConsentsKeys().filter((key) => {
      if (this.isRegistrationScreen) {
        return schema.consents.showAtRegistration(key);
      }
      return schema.consents.showInProfile(key);
    }).forEach((key) => {
      if (!this.allowedKeys || this.allowedKeys.includes(key)) {
        this.consentKeys.push(key);

        const validators: ValidatorFn[] = [];
        if (schema.consents.isRequired(key)) { validators.push(Validators.required); }
        const formControl = new UntypedFormControl(undefined, validators);

        this.formGroup.addControl(key, formControl);
      }
    });

    this.onChange(this.formGroup.value);
  }

  public openModal(template: TemplateRef<any>, config): void {
    this.readmoreModalRef = this.modalService.show(template, config);
  }

  public hideModal(): void {
    this.readmoreModalRef.hide();
  }

  public writeValue(obj: any): void {
    this.formGroup.patchValue(obj);
    this.cdr.markForCheck();
  }

  public validate(): ValidationErrors | any {
    this.consentKeys.forEach(key => {
      const errors = this.formGroup.get(key)?.errors;
      if (errors) {
        this.formGroup.setErrors(errors);
      }
    });

    return this.formGroup.errors;
  }

  protected selectAll(): void {
    Object.keys(this.formGroup.controls).forEach(key =>
      this.formGroup.controls[key].patchValue(true),
    );
  }

  protected setConsent(key: string, value: boolean): void {
    this.formGroup.patchValue({
      [key]: value,
    });

    this.cdr.markForCheck();
  }
}
